import React, { useRef, useState } from "react";
import { colord } from "colord";
import { Text, ColorSelect, Box, Icon } from "@showitinc/showit-wc";
import { ALPHA, ColorPicker as ColorPickerInput } from "@showitinc/color-picker";

import useStoreState from "../../components/providers/useStoreState";
import DesignThemeStore from "../../stores/DesignThemeStore";
import AppConstants from "../../constants/AppConstants";
import styles from "./ColorPicker.module.css";
import { FLYOUT_SHIFT_LEFT } from "../../constants/UIConstants";
import Flyout from "./Flyout";
import { MIXED } from "../../constants/DesignConstants.mjs";

const { DEFAULT_PALETTE, TRANSPARENT } = AppConstants;
const PALETTE_PREFIX = "colors-";

const isPaletteColor = (color) => color && color.startsWith(PALETTE_PREFIX);

const getPaletteIndex = (color) =>
  isPaletteColor(color)
    ? Number(color.slice(PALETTE_PREFIX.length, PALETTE_PREFIX.length + 1))
    : -1;

export const getDisplayColor = (paletteOptions, color) => {
  const paletteIndex = getPaletteIndex(color);
  const [originalValue, alpha = 100] = color.split(":");
  const colorObj = colord(paletteIndex > -1 ? paletteOptions[paletteIndex] : originalValue).alpha(
    Number(alpha) * 0.01
  );
  return colorObj;
};

const LibraryView = ({ palette, paletteIndex, onColorChange }) => (
  <Box d="flex" justify="between" className={styles["swatch-main-wrapper"]}>
    {palette.map((colorValue, i) => (
      <Box
        className={styles.swatchContainer}
        key={`color-${i}`}
        d="flex"
        flex="col"
        align="center"
        onClick={() => onColorChange(`${PALETTE_PREFIX}${i}`)}
      >
        <div className={styles.swatch} style={{ backgroundColor: colorValue }}>
          <Text className={styles.themeIndex}>{i + 1}</Text>
          {paletteIndex === i ? <Icon className={styles.selectedIcon} type="check" /> : null}
        </div>
      </Box>
    ))}

    <Box
      className={styles.swatchContainer}
      key="transparent"
      d="flex"
      flex="col"
      align="center"
      onClick={() => onColorChange(`${TRANSPARENT}`)}
    >
      <div className={`${styles.swatch} ${styles.transparent}`} />
    </Box>
  </Box>
);

const formatToColorEvent = (onChange) => (e) => {
  const alpha = e.detail.value;
  delete e.detail.value;
  e.detail.changed = ALPHA;
  e.detail.alpha = alpha;
  onChange(e);
};

/**
 * ColorPicker component for selecting and displaying colors.
 *
 * @param {Object} props
 * @param {string} props.color - The initial color value. Defaults to TRANSPARENT.
 * @param {boolean} props.context - Whether to use context.
 * @param {Array} props.customPalette - Custom palette of colors.
 * @param {boolean} props.disabled - Whether the color picker is disabled.
 * @param {boolean} props.eyedropper - Whether to show the eyedropper tool. Defaults to true.
 * @param {boolean} props.hideAlpha - Whether to hide the alpha slider.
 * @param {string} props.id - The id of the color select element.
 * @param {string} props.label - The label for the color picker. Defaults to "Color".
 * @param {function} props.onSIBeforeChange - Callback function before the color changes.
 * @param {function} props.onSIChange - Callback function when the color changes.
 * @param {function} props.onSIInput - Callback function when the color input changes.
 * @param {boolean} props.reserveContext - Whether to reserve context space.
 * @param {string} props.shift - The shift direction for the flyout. Defaults to FLYOUT_SHIFT_LEFT.
 * @param {boolean} props.themeOnly - Whether to restrict to theme colors only.
 * @returns {JSX.Element} The ColorPicker component.
 */
const ColorPicker = ({
  color = TRANSPARENT,
  context,
  customPalette,
  disabled,
  eyedropper = true,
  hideAlpha,
  "data-testid": dataTestId,
  id,
  label = "Color",
  onSIBeforeChange,
  onSIChange,
  onSIInput,
  reserveContext,
  shift = FLYOUT_SHIFT_LEFT,
  themeOnly,
}) => {
  const inputRef = useRef();
  const [flyoutRef, setFlyoutRef] = useState(null);

  const designColors = useStoreState(DesignThemeStore, DesignThemeStore.getColors);
  const palette = customPalette ?? designColors?.toJS() ?? DEFAULT_PALETTE;
  const paletteIndex = getPaletteIndex(color);
  const displayColor = getDisplayColor(palette, color);

  const _openPicker = () => !disabled && flyoutRef && flyoutRef.showPopover(inputRef.current);

  return (
    <>
      <ColorSelect
        hideAlpha={hideAlpha}
        id={id}
        data-testid={dataTestId}
        context={context}
        disabled={disabled}
        label={label}
        className={styles.colorSelect}
        mixed={color?.startsWith(MIXED)}
        mixedAlpha={color?.endsWith(`:${MIXED}`)}
        onSIToggleColorSwatch={_openPicker}
        onSIBeforeChange={formatToColorEvent(onSIBeforeChange)}
        onSIInput={formatToColorEvent(onSIInput)}
        ref={inputRef}
        reserveContext={reserveContext}
        value={`${displayColor.alpha(1).toHex()}:${(displayColor.alpha() * 100).toFixed()}`}
        librarySwatch={paletteIndex + 1}
      />
      <Flyout title="Color style" refSetter={setFlyoutRef} shift={shift} className={styles.flyout}>
        <LibraryView onColorChange={onSIChange} palette={palette} paletteIndex={paletteIndex} />
        {!themeOnly && (
          <ColorPickerInput
            color={displayColor.toHex()}
            eyedropper={eyedropper}
            onSIBeforeChange={onSIBeforeChange}
            onSIInput={onSIInput}
          />
        )}
      </Flyout>
    </>
  );
};

export default ColorPicker;
